import { useEffect, useState, useCallback } from "react";
import { toast } from "react-hot-toast";
import {
  FileText,
  ChevronLeft,
  ChevronRight,
  Search,
  Filter,
  X,
  TrendingUp,
  CheckCircle2,
  Clock,
  AlertCircle,
  BarChart3,
  PieChart as PieChartIcon,
  Activity,
} from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Label,
} from "recharts";
import documentProcessReportService, {
  DocumentItem,
  DocumentProcessReportResponse,
} from "../../services/document-process-report.service";
import Loader from "../../widgets/Loader";

interface StatsCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  trend?: string;
  percentage?: string;
}

const StatsCard = ({
  title,
  value,
  icon,
  color,
  bgColor,
  trend,
  percentage,
}: StatsCardProps) => (
  <div className="group bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md hover:-translate-y-1 transition-all duration-300">
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-2">
          <p className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
            {title}
          </p>
        </div>
        <h3 className="text-3xl font-bold text-gray-900 mb-2">{value}</h3>
        {trend && percentage && (
          <div className="flex items-center gap-2">
            <span
              className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${
                percentage?.startsWith("+")
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-red-50 text-red-700"
              }`}
            >
              <TrendingUp className="w-3 h-3" />
              {percentage}
            </span>
            <span className="text-xs text-gray-500">{trend}</span>
          </div>
        )}
      </div>
      <div
        className={`p-4 rounded-2xl ${bgColor} group-hover:scale-110 transition-transform duration-300`}
      >
        <div className={color}>{icon}</div>
      </div>
    </div>
  </div>
);

interface ChartCardProps {
  title: string | React.ReactNode;
  icon: React.ReactNode;
  children: React.ReactNode;
}

const ChartCard = ({ title, icon, children }: ChartCardProps) => (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-lg transition-shadow duration-300">
    <div className="flex items-center gap-3 mb-6">
      <div className="p-2 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg">
        {icon}
      </div>
      <h3 className="text-lg font-bold text-gray-900">{title}</h3>
    </div>
    {children}
  </div>
);

const DocumentProcessingReport = () => {
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [, setAllDocuments] = useState<DocumentItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingCharts, setIsLoadingCharts] = useState<boolean>(false);
  const [showFilters, setShowFilters] = useState<boolean>(true);

  // Pagination
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [pageSize] = useState<number>(10);
  const totalPages = Math.ceil(totalCount / pageSize);

  // Search filters
  const [folderName, setFolderName] = useState<string>("");
  const [storeType, setStoreType] = useState<string>("");

  // Debounced values
  const [debouncedFolderName, setDebouncedFolderName] = useState<string>("");
  const [debouncedStoreType, setDebouncedStoreType] = useState<string>("");
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  // Statistics and Chart Data
  const [stats, setStats] = useState({
    total: 0,
    done: 0,
    pending: 0,
    failed: 0,
    donePercentage: "0%",
    pendingPercentage: "0%",
    failedPercentage: "0%",
  });

  const [chartData, setChartData] = useState({
    statusDistribution: [] as any[],
    uploadTypes: [] as any[],
    aiLevels: [] as any[],
    processingTrend: [] as any[],
    storeTypes: [] as any[],
  });

  // Chart Colors
  const STATUS_COLORS = {
    DONE: "#10b981",
    PENDING: "#f59e0b",
    FAILED: "#ef4444",
  };

  const AI_LEVEL_COLORS = {
    HIGH: "#8b5cf6",
    MEDIUM: "#3b82f6",
    LOW: "#6b7280",
  };

  // Debounce effect
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedFolderName(folderName);
      setDebouncedStoreType(storeType);
      setCurrentPage(1);
    }, 600);

    return () => clearTimeout(handler);
  }, [folderName, storeType]);

  // ✅ Fetch ALL documents for charts (without filters/pagination)
  const fetchAllDocumentsForCharts = useCallback(async () => {
    try {
      setIsLoadingCharts(true);

      // Send empty payload to get all documents
      const payload = {
        skip: 0,
        limit: 0, // Large limit to get all data
      };

      const response: DocumentProcessReportResponse =
        await documentProcessReportService.getDocumentProcessReport(payload);

      if (response?.success) {
        const allDocs = response?.data?.documents || [];
        setAllDocuments(allDocs);

        // Calculate real statistics from API response
        const totalDocs = response?.data?.total_count || 0;
        const doneCount = allDocs?.filter(
          (doc: any) => doc?.overall_processed_status === "DONE"
        ).length;
        const pendingCount = allDocs.filter((doc: any) =>
          ["PENDING", "PROCESSING", "IN_PROGRESS"].includes(doc?.overall_processed_status)
        ).length;

        const failedCount = allDocs.filter((doc: any) =>
          doc?.overall_processed_status !== "DONE" &&
          !["PENDING", "PROCESSING", "IN_PROGRESS"].includes(doc?.overall_processed_status)
        ).length;

        // Calculate percentages
        const donePercentage =
          totalDocs > 0
            ? `${((doneCount / totalDocs) * 100).toFixed(1)}%`
            : "0%";
        const pendingPercentage =
          totalDocs > 0
            ? `${((pendingCount / totalDocs) * 100).toFixed(1)}%`
            : "0%";
        const failedPercentage =
          totalDocs > 0
            ? `${((failedCount / totalDocs) * 100).toFixed(1)}%`
            : "0%";

        setStats({
          total: totalDocs,
          done: doneCount,
          pending: pendingCount,
          failed: failedCount,
          donePercentage,
          pendingPercentage,
          failedPercentage,
        });

        // ✅ 1. Status Distribution (from real data)
        const statusData = [
          { name: "Completed", value: doneCount, color: STATUS_COLORS.DONE },
          {
            name: "Pending",
            value: pendingCount,
            color: STATUS_COLORS.PENDING,
          },
          { name: "Failed", value: failedCount, color: STATUS_COLORS.FAILED },
        ].filter((item: any) => item?.value > 0);

        // ✅ 2. Upload Types Distribution (from real data)
        const uploadTypesMap = new Map<string, number>();
        allDocs.forEach((doc: any) => {
          const type = doc?.upload_type || "Others";
          uploadTypesMap.set(type, (uploadTypesMap.get(type) || 0) + 1);
        });
        const uploadTypesData = Array.from(uploadTypesMap.entries())
          .map(([name, value]) => ({ name, value }))
          .sort((a, b) => b.value - a.value);

        // ✅ 3. AI Performance Levels (from real data)
        const aiLevelsMap = new Map<string, number>();
        allDocs.forEach((doc: any) => {
          const level = doc?.ai_performance_level || "Unknown";
          aiLevelsMap.set(level, (aiLevelsMap.get(level) || 0) + 1);
        });
        const aiLevelsData = Array.from(aiLevelsMap.entries())
          .map(([name, value]) => ({
            name,
            value,
            fill:
              AI_LEVEL_COLORS[name as keyof typeof AI_LEVEL_COLORS] ||
              "#6b7280",
          }))
          .sort((a, b) => b.value - a.value);

        // ✅ 4. Store Types Distribution (from real data)
        const storeTypesMap = new Map<string, number>();
        allDocs.forEach((doc: any) => {
          const storeType =
            doc?.document_store?.document_store_type || "Unknown";
          storeTypesMap.set(storeType, (storeTypesMap.get(storeType) || 0) + 1);
        });
        const storeTypesData = Array.from(storeTypesMap.entries())
          .map(([name, value]) => ({ name, value }))
          .sort((a, b) => b.value - a.value);

        // ✅ 5. Processing Trend by Date (from real data)
        const trendMap = new Map<
          string,
          { total: number; processed: number }
        >();

        allDocs.forEach((doc: any) => {
          const date = new Date(doc?.createdAt).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          });

          if (!trendMap.has(date)) {
            trendMap.set(date, { total: 0, processed: 0 });
          }

          const current = trendMap.get(date)!;
          current.total += 1;
          if (doc.overall_processed_status === "DONE") {
            current.processed += 1;
          }
        });

        const trendData = Array.from(trendMap.entries())
          .map(([date, counts]) => ({
            date,
            documents: counts.total,
            processed: counts.processed,
          }))
          .sort((a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);
            return dateA.getTime() - dateB.getTime();
          })
          .slice(-7); // Last 7 days

        setChartData({
          statusDistribution: statusData,
          uploadTypes: uploadTypesData,
          aiLevels: aiLevelsData,
          processingTrend: trendData,
          storeTypes: storeTypesData,
        });
      } else {
        toast.error("Failed to load chart data");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error fetching chart data");
    } finally {
      setIsLoadingCharts(false);
    }
  }, []);

  // ✅ Fetch paginated/filtered documents for table
  const fetchDocuments = useCallback(async () => {
    try {
      setIsLoading(true);
      const payload = {
        skip: (currentPage - 1) * pageSize,
        limit: pageSize,
        ...(debouncedFolderName ? { folder_name: debouncedFolderName } : {}),
        ...(debouncedStoreType
          ? { document_store_type: debouncedStoreType.toUpperCase() }
          : {}),
      };

      const response: DocumentProcessReportResponse =
        await documentProcessReportService.getDocumentProcessReport(payload);

      if (response?.success) {
        setDocuments(response?.data?.documents || []);
        setTotalCount(response?.data?.total_count || 0);
      } else {
        toast.error("Failed to load document reports");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error fetching document reports");
    } finally {
      setIsLoading(false);
    }
  }, [debouncedFolderName, debouncedStoreType, currentPage, pageSize]);

  // Load chart data on component mount
  useEffect(() => {
    fetchAllDocumentsForCharts();
  }, [fetchAllDocumentsForCharts]);

  // Fetch table data when page or filters change
  useEffect(() => {
    fetchDocuments();
  }, [currentPage, fetchDocuments]);

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const handleClearFilters = () => {
    setFolderName("");
    setStoreType("");
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "DONE":
        return "bg-emerald-50 text-emerald-700 border border-emerald-200";
      case "PENDING":
        return "bg-amber-50 text-amber-700 border border-amber-200";
      default:
        return "bg-red-50 text-red-700 border border-red-200";
    }
  };

  const getAILevelColor = (level: string) => {
    switch (level) {
      case "HIGH":
        return "bg-purple-50 text-purple-700 border border-purple-200";
      case "MEDIUM":
        return "bg-blue-50 text-blue-700 border border-blue-200";
      case "LOW":
        return "bg-red-50 text-red-700 border border-red-200";
      default:
        return "bg-gray-50 text-gray-700 border border-gray-200";
    }
  };

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      // payload[0].payload contains the original data point
      const data = payload[0].payload;

      return (
        <div className="bg-white px-4 py-3 rounded-lg shadow-lg border border-gray-200">
          <p className="text-sm font-semibold text-gray-900">{data.name}</p>
          <p className="text-sm text-gray-600">
            Count: <span className="font-bold">{data.value}</span>
          </p>
        </div>
      );
    }
    return null;
  };

  const ProcessingChartTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const documents = payload.find(
        (item: any) => item.dataKey === "documents"
      );
      const processed = payload.find(
        (item: any) => item.dataKey === "processed"
      );

      return (
        <div className="bg-white border border-gray-200 rounded-md shadow-md p-3 text-sm">
          <p className="font-medium text-gray-800 mb-1">{label}</p>
          {documents && (
            <p className="text-blue-500">
              📄 Total Documents:{" "}
              <span className="font-semibold">{documents.value}</span>
            </p>
          )}
          {processed && (
            <p className="text-emerald-600">
              ⚙️ Processed:{" "}
              <span className="font-semibold">{processed.value}</span>
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white border-slate-200 border-b mb-4">
        <div className="px-6 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-xl font-bold ">
                Document Processing Analytics
              </h1>
              <p className="flex items-center gap-2 text-xs text-slate-600">
                <Activity className="w-4 h-4" />
                Real-time monitoring and insights dashboard
              </p>
            </div>
            <div className="flex items-center gap-3"></div>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatsCard
          title="Total Documents"
          value={stats?.total}
          icon={<FileText className="w-7 h-7" />}
          color="text-blue-600"
          bgColor="bg-gradient-to-br from-blue-50 to-blue-100"
        />
        <StatsCard
          title="Completed"
          value={stats?.done}
          icon={<CheckCircle2 className="w-7 h-7" />}
          color="text-emerald-600"
          bgColor="bg-gradient-to-br from-emerald-50 to-emerald-100"
          trend="success rate"
          percentage={stats?.donePercentage}
        />
        <StatsCard
          title="In Progress"
          value={stats?.pending}
          icon={<Clock className="w-7 h-7" />}
          color="text-amber-600"
          bgColor="bg-gradient-to-br from-amber-50 to-amber-100"
          trend="pending"
          percentage={stats?.pendingPercentage}
        />
        <StatsCard
          title="Failed"
          value={stats?.failed}
          icon={<AlertCircle className="w-7 h-7" />}
          color="text-red-600"
          bgColor="bg-gradient-to-br from-red-50 to-red-100"
          trend="error rate"
          percentage={stats?.failedPercentage}
        />
      </div>

      {/* Charts Section */}
      {isLoadingCharts ? (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 mb-8">
          <div className="flex flex-col items-center justify-center gap-4">
            <Loader />
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Status Distribution - Donut Chart */}
          <ChartCard
            title={
              <span className="text-lg font-semibold text-gray-800">
                Processing Status Distribution
              </span>
            }
            icon={<PieChartIcon className="w-5 h-5 text-blue-600" />}
          >
            {chartData?.statusDistribution?.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={chartData?.statusDistribution}
                      cx="50%"
                      cy="50%"
                      innerRadius={70}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, value }: any) => `${name}  ${value}`}
                      onMouseEnter={(_, index) => setActiveIndex(index)}
                      onMouseLeave={() => setActiveIndex(null)}
                      isAnimationActive={
                        !activeIndex // ✅ only animate when not hovering
                      }
                      animationDuration={600} // ✅ smooth animation when data loads
                      animationBegin={0}
                    >
                      {chartData?.statusDistribution?.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={entry.color}
                          style={{
                            transition: "opacity 0.25s ease-in-out",
                          }}
                          opacity={
                            activeIndex === null || activeIndex === index
                              ? 1
                              : 0.5
                          }
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>

                {/* ✅ Interactive Legend */}
                <div className="flex items-center justify-center gap-6 mt-4">
                  {chartData?.statusDistribution?.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 cursor-pointer transition-all"
                      onMouseEnter={() => setActiveIndex(index)} // highlight on hover
                      onMouseLeave={() => setActiveIndex(null)} // reset on leave
                    >
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{
                          backgroundColor: item.color,
                          opacity:
                            activeIndex === null || activeIndex === index
                              ? 1
                              : 0.4,
                        }}
                      ></div>
                      <span
                        className={`text-sm ${
                          activeIndex === index
                            ? "text-gray-800 font-medium"
                            : "text-gray-600"
                        }`}
                      >
                        {item.name}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-gray-400">
                No data available
              </div>
            )}
          </ChartCard>

          {/* Upload Types - Bar Chart */}
          <ChartCard
            title={<span className="text-lg font-semibold text-gray-800">Documents by Upload Type</span>}
            icon={<BarChart3 className="w-5 h-5 text-indigo-600" />}
          >
            {chartData?.uploadTypes?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={chartData?.uploadTypes}
                  margin={{ top: 10, right: 20, left: 20, bottom: 40 }} // ✅ extra bottom margin for X label
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />

                  {/* ✅ X-Axis with Label */}
                  <XAxis dataKey="name" tick={{ fontSize: 12 }}>
                    <Label
                      value="Upload Type"
                      position="bottom"
                      offset={20}
                      style={{
                        fill: "#4B5563", // gray-600
                        fontSize: 13,
                        fontWeight: 500,
                      }}
                    />
                  </XAxis>

                  {/* ✅ Y-Axis with Label */}
                  <YAxis tick={{ fontSize: 12 }}>
                    <Label
                      value="Document Count"
                      angle={-90}
                      position="insideLeft"
                      style={{
                        textAnchor: "middle",
                        fill: "#4B5563",
                        fontSize: 13,
                        fontWeight: 500,
                      }}
                    />
                  </YAxis>

                  <Tooltip content={<CustomTooltip />} />

                  <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                    {chartData?.uploadTypes?.map((_, index): any => (
                      <Cell
                        key={`cell-${index}`}
                        fill={`hsl(${220 + index * 30}, 70%, 60%)`}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-gray-400">
                No data available
              </div>
            )}
          </ChartCard>

          {/* AI Performance Levels */}
          <ChartCard
            title={<span className="text-lg font-semibold text-gray-800">AI Performance Distribution</span>} 
            icon={<Activity className="w-5 h-5 text-purple-600" />}
          >
            {chartData?.aiLevels?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={chartData?.aiLevels}
                  layout="horizontal"
                  margin={{ top: 10, right: 20, left: 20, bottom: 40 }} // ✅ added bottom margin for X-axis label
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />

                  {/* ✅ X-Axis with Label */}
                  <XAxis dataKey="name" tick={{ fontSize: 12 }}>
                    <Label
                      value="AI Level"
                      position="bottom"
                      offset={20}
                      style={{
                        fill: "#4B5563", // gray-600
                        fontSize: 13,
                        fontWeight: 500,
                      }}
                    />
                  </XAxis>

                  {/* ✅ Y-Axis with Label */}
                  <YAxis tick={{ fontSize: 12 }}>
                    <Label
                      value="Performance Count"
                      angle={-90}
                      position="insideLeft"
                      style={{
                        textAnchor: "middle",
                        fill: "#4B5563",
                        fontSize: 13,
                        fontWeight: 500,
                      }}
                    />
                  </YAxis>

                  <Tooltip content={<CustomTooltip />} />

                  <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                    {chartData?.aiLevels?.map((entry, index): any => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-gray-400">
                No data available
              </div>
            )}
          </ChartCard>

          {/* Processing Trend - Area Chart */}
          <ChartCard
            title={<span className="text-lg font-semibold text-gray-800">Processing Trend (Last 7 Days)</span>}
            icon={<TrendingUp className="w-5 h-5 text-emerald-600" />}
          >
            {chartData?.processingTrend?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart
                  data={chartData?.processingTrend}
                  margin={{ top: 10, right: 30, left: 30, bottom: 50 }} // ✅ Added extra bottom space for X-label
                >
                  <defs>
                    <linearGradient
                      id="colorDocuments"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient
                      id="colorProcessed"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>

                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />

                  {/* ✅ X-Axis with Label */}
                  <XAxis dataKey="date" tick={{ fontSize: 12 }}>
                    <Label
                      value="Date"
                      position="bottom"
                      offset={30}
                      style={{
                        fill: "#4B5563", // text-gray-600
                        fontSize: 13,
                        fontWeight: 500,
                      }}
                    />
                  </XAxis>

                  {/* ✅ Y-Axis with Label */}
                  <YAxis tick={{ fontSize: 12 }}>
                    <Label
                      value="Processing Count"
                      angle={-90}
                      position="insideLeft"
                      style={{
                        textAnchor: "middle",
                        fill: "#4B5563",
                        fontSize: 13,
                        fontWeight: 500,
                      }}
                    />
                  </YAxis>

                  {/* ✅ Tooltip shows both lines */}
                  <Tooltip content={<ProcessingChartTooltip />} />
                  <Legend verticalAlign="bottom" height={36} />

                  {/* ✅ Area for Total Documents */}
                  <Area
                    type="monotone"
                    dataKey="documents"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorDocuments)"
                    name="Total Documents"
                  />

                  {/* ✅ Area for Processed Documents */}
                  <Area
                    type="monotone"
                    dataKey="processed"
                    stroke="#10b981"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorProcessed)"
                    name="Processed"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-gray-400">
                No data available
              </div>
            )}
          </ChartCard>
        </div>
      )}

      {/* Filters Section */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 mb-6 overflow-hidden">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
        >
          {/* Left content */}
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Filter className="w-5 h-5 text-blue-600" />
            </div>
            <div className="text-left">
              <h3 className="font-bold text-lg text-gray-900">Advanced Filters</h3>
              <p className="text-xs text-gray-500">
                Refine your search criteria
              </p>
            </div>
            {(folderName || storeType) && (
              <span className="px-3 py-1 bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-700 text-xs rounded-full font-semibold">
                {[folderName, storeType].filter(Boolean).length} active
              </span>
            )}
          </div>

          {/* Right content: Clear button + Chevron */}
          <div className="flex items-center gap-3">
            {showFilters && (
              <button
                onClick={(e) => {
                  e.stopPropagation(); // prevent toggling showFilters
                  handleClearFilters();
                }}
                disabled={!folderName && !storeType}
                className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Clear All
              </button>
            )}
            <ChevronRight
              className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${
                showFilters ? "rotate-90" : ""
              }`}
            />
          </div>
        </button>

        {showFilters && (
          <div className="px-6 pb-6 border-t border-gray-100 bg-gradient-to-br from-gray-50 to-white">
            {/* ✅ Responsive Grid - 1 column on small screens, 2 on medium and above */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-5">
              {/* 🔹 Folder Name Filter */}
              <div className="relative">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Folder Name
                </label>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by folder name..."
                    value={folderName}
                    onChange={(e) => setFolderName(e.target.value)}
                    className="pl-10 pr-10 py-2 w-full border border-gray-300 rounded-lg 
                     focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                  {folderName && (
                    <button
                      onClick={() => setFolderName("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 bg-gray-100 rounded-full p-1"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>

              {/* 🔹 Document Store Type Filter */}
              <div className="relative">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Document Store Type
                </label>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by store type..."
                    value={storeType}
                    onChange={(e) => setStoreType(e.target.value)}
                    className="pl-10 pr-10 py-2 w-full border border-gray-300 rounded-lg 
                     focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                  {storeType && (
                    <button
                      onClick={() => setStoreType("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 bg-gray-100 rounded-full p-1"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 bg-white to-white border-b border-gray-100">
          <h3 className="text-lg font-bold text-gray-900">Document Records</h3>
          <p className="text-sm text-gray-600 mt-1">
            Detailed list of all processed documents
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gradient-to-r from-gray-50 to-slate-50">
              <tr>
                {[
                  "Document",
                  "Time Taken",
                  "Processing",
                  "AI Level",
                  "Status",
                  "Folder",
                  "Store Type",
                  "Uploaded By",
                  "Date",
                ].map((header, idx) => (
                  <th
                    key={idx}
                    className={`px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap ${
                      idx === 9 ? "text-right" : ""
                    }`}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="bg-white divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td colSpan={10} className="px-6 py-20 text-center">
                    <div className="flex flex-col items-center justify-center gap-4">
                      <Loader />
                    </div>
                  </td>
                </tr>
              ) : documents.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-6 py-20 text-center">
                    <div className="flex flex-col items-center justify-center gap-4">
                      <div className="p-5 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl">
                        <FileText className="w-10 h-10 text-gray-400" />
                      </div>
                      <div>
                        <p className="text-base font-bold text-gray-900">
                          No documents found
                        </p>
                        <p className="text-sm text-gray-500 mt-2">
                          Try adjusting your filters or search criteria
                        </p>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                documents?.map((doc: any) => (
                  <tr
                    key={doc?._id}
                    className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-200 group"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl group-hover:from-blue-100 group-hover:to-indigo-100 transition-all">
                          <FileText className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="max-w-[240px]">
                          <p className="text-sm font-semibold text-gray-900 truncate">
                            {doc?.name}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {(doc?.file_size / 1024).toFixed(2)} KB
                          </p>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <span className="text-sm font-medium text-gray-700">
                        {doc?.overall_processing?.time_taken
                          ? doc?.overall_processing?.time_taken.toFixed(2) +
                            " sec"
                          : "--"}
                      </span>
                    </td>

                    <td className="px-6 py-4">
                      <span className="text-sm font-medium text-gray-700">
                        {doc?.file_processing_type}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold ${getAILevelColor(
                          doc?.ai_performance_level
                        )}`}
                      >
                        {doc?.ai_performance_level}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold ${getStatusColor(
                          doc?.overall_processed_status
                        )}`}
                      >
                        {doc?.overall_processed_status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm font-medium text-gray-700 max-w-[150px] block truncate">
                        {doc?.document_store?.folder_name || "--"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm font-medium text-gray-700">
                        {doc?.document_store?.document_store_type || "--"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm font-medium text-gray-700 truncate max-w-[150px] block">
                        {doc?.uploaded_by?.user_name || "--"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-gray-700">
                        {new Date(doc?.createdAt).toLocaleDateString()}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        {totalPages > 0 && !isLoading && (
          <div className="px-6 py-5 border-t border-gray-200 bg-gradient-to-r from-gray-50 to-white">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-gray-700">
                Showing{" "}
                <span className="font-bold text-gray-900">
                  {(currentPage - 1) * pageSize + 1}
                </span>{" "}
                to{" "}
                <span className="font-bold text-gray-900">
                  {Math.min(currentPage * pageSize, totalCount)}
                </span>{" "}
                of <span className="font-bold text-gray-900">{totalCount}</span>{" "}
                results
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  type="button"
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                {/* Page Numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      type="button"
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        currentPage === pageNum
                          ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                          : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  type="button"
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentProcessingReport;
